import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { setAccountSettingsTab } from "store/tempDataSlise"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconAccountSettings } from "assets/svg/modal-icon-account-settings.svg"

import { checkRoleType } from "utils/checkRoleType"
import { isPartnersEnabledPlan } from "utils/isPartnersEnabledPlan"
import { AccountSettingsThemes } from "modals/AccountSettingsThemes"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import TabsWrapper, { TabItem } from "components/common/TabsWrapper"
import { AccountSettingsTabId } from "pages/account/AccountSettingsPage/config"

import { AccountSettingsAdditionalInfo } from "../AccountSettingsAdditionalInfo"
import { AccountSettingsIntegration } from "../AccountSettingsIntegration"
import { AccountSettingsNotifications } from "../AccountSettingsNotifications"
import { AccountSettingsPartners } from "../AccountSettingsPartners"
import { AccountSettingsPassword } from "../AccountSettingsPassword"
import { AccountSettingsProfile } from "../AccountSettingsProfile"
import AccountSettingsBilling from "./AccountSettingsBilling"

import styles from "./index.module.scss"

const StyledTabsWrapper = styled(TabsWrapper)`
  margin-bottom: 0;

  > div {
    flex: 1;
  }
`

interface Props {
  onCancel: () => void
}

export const AccountSettingsModal = ({ onCancel }: Props) => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const accountSettingsTab = useAppSelector(state => state.tempData.accountSettingsTab)
  const current_subscription = userProfile?.contractor?.subscription_plan
  const hasEPlusPlan = current_subscription === SubscriptionPlan.E_PLUS
  const partnersEnabled = isPartnersEnabledPlan(current_subscription)
  const tabs: TabItem[] = useMemo(
    () => [
      { id: AccountSettingsTabId.PROFILE, label: "Profile" },
      { id: AccountSettingsTabId.ADDITIONAL_INFORMATION, label: "Additional information" },
      ...(partnersEnabled ? [{ id: AccountSettingsTabId.PARTNERS, label: "Partners" }] : []),
      { id: AccountSettingsTabId.BILLING, label: "Billing" },
      { id: AccountSettingsTabId.PASSWORD, label: "Password" },
      { id: AccountSettingsTabId.INTEGRATIONS, label: "Integration" },
      ...(hasEPlusPlan ? [{ id: AccountSettingsTabId.THEMES, label: "Themes" }] : []),
      { id: AccountSettingsTabId.NOTIFICATION_SETTINGS, label: "Notifications Settings" },
    ],
    [hasEPlusPlan, partnersEnabled],
  )
  const tabIndex = tabs.findIndex(tab => tab.id === accountSettingsTab)
  const [currentTab, setCurrentTab] = useState<number>(tabIndex !== -1 ? tabIndex : 0)
  const userRole = checkRoleType(userProfile?.type)
  const isTechnician = userRole === "contractor-technician"
  const isEngineer = userRole === "contractor-engineer"
  const shouldHideTabs = isTechnician || isEngineer

  useEffect(() => {
    if (accountSettingsTab !== undefined) {
      dispatch(setAccountSettingsTab(undefined))
    }
  }, [accountSettingsTab, dispatch])

  const renderCurrentView = () => {
    if (shouldHideTabs) {
      return <AccountSettingsNotifications />
    }

    const tab = tabs[currentTab]
    switch (tab.id) {
      case AccountSettingsTabId.ADDITIONAL_INFORMATION:
        return <AccountSettingsAdditionalInfo onCancel={onCancel} />
      case AccountSettingsTabId.PASSWORD:
        return <AccountSettingsPassword onCancel={onCancel} />
      case AccountSettingsTabId.INTEGRATIONS:
        return <AccountSettingsIntegration />
      case AccountSettingsTabId.BILLING:
        return <AccountSettingsBilling />
      case AccountSettingsTabId.PARTNERS:
        return <AccountSettingsPartners />
      case AccountSettingsTabId.THEMES:
        return <AccountSettingsThemes />
      case AccountSettingsTabId.NOTIFICATION_SETTINGS:
        return <AccountSettingsNotifications />
      default:
        return <AccountSettingsProfile onCancel={onCancel} />
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout width="880px" isWithoutFooter={true} onCancel={onCancel}>
          <ModalHeader title={`${shouldHideTabs ? "Notifications" : "Account"} Settings`}>
            <IconAccountSettings />
          </ModalHeader>
          <div>
            {!shouldHideTabs && (
              <div className={styles.modalTabsWrapper}>
                <StyledTabsWrapper tabs={tabs} currentTab={currentTab} onTabChange={setCurrentTab} expandable={true} />
              </div>
            )}
            {renderCurrentView()}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
